import { BufferGeometry } from "three";
import { LineMaterial } from "../render/LineMaterial";
import { getMaterialTextureMask } from "./main/MaterialUniforms";
import { USE_WBOIT } from "../globals";


import { registerDepthBias } from "./main/DepthBiasMap";

const BUFFER_LAYOUT_BITS = 19n;
const MATERIAL_BITS = 18n;

export function getMaterialHash(material, hasTexures) {
  let mask = 0;

  //two bits
  if (material.depthWrite) {
    mask |= 0x1;
  }
  if (material.depthTest) {
    mask |= 0x2;
  }

  //three bits
  if (material.depthFunc) {
    switch (material.depthFunc) {
      case "less":mask |= 1 << 2;break;
      case "less-equal":mask |= 2 << 2;break;
      case "greater":mask |= 3 << 2;break;
      case "greater-equal":mask |= 4 << 2;break;
      case "never":mask |= 5 << 2;break;
      case "always":mask |= 6 << 2;break;
      default:console.warn("unsupported depthFunc", material.depthFunc);
    }
  } else {
    //default is less-equal
    mask |= 2 << 2;
  }

  //two bits
  mask |= (material.side || 0) << 5;

  //one bit
  mask |= hasTexures ? 1 << 7 : 0;

  //one bit
  mask |= material.isRoomMaterial && material.heatmapSensorCount ? 1 << 8 : 0;

  // one bit: is the material unlit?
  mask |= material instanceof THREE.MeshBasicMaterial ? 1 << 9 : 0;

  // one bit
  mask |= material.irradianceMap ? 1 << 10 : 0;

  // 6 bits for depthBias index (0-63) - computed from global map
  const depthBiasIndex = registerDepthBias(material);
  mask |= (depthBiasIndex & 0x3F) << 11;

  // one bit: is the material metallic?
  mask |= material.metal ? 1 << 17 : 0;

  // NOTE: Whenever adding new bits here, make sure to update MATERIAL_BITS to the new number of required bits.

  material.__gpuPipelineHash = mask;
  return mask;
}

/** Wideline geometry is only considered valid if it contains all supported extra vertex attributes as a part of the interleaved vertex buffer. */
export function isValidWideLineGeometry(geom) {
  const attribs = geom.attributes;
  const prevValid = attribs.prev?.offset != null;
  const nextValid = attribs.next?.offset != null;
  const sideValid = attribs.side?.offset != null;
  return geom.isWideLines && geom.vb && prevValid && nextValid && sideValid;
}


export function getBufferLayoutHash(geometry, includeUV, includeVC, includeWideLines) {

  //TODO: currently set up to only memorize one configuration, so includeUV and includeVC cannot
  //vary across uses of the geometry. This can be an issue if a geometry is used from both a textured
  //and non-textured pipeline, for example, but for now is avoided by the caller by always configuring
  //the pipeline in the same way (i.e. the pipeline also depends on the includeUV/VC flag).

  // Note: geometry.__gpuPipelineHash only encodes the geometry buffer layout, but it's combined into a single value
  // with the material mask in getPipelineHash, so we can only use the lowest/first BUFFER_LAYOUT_BITS bits
  // (remaining are used for the materials mask).
  let mask = geometry.__gpuPipelineHash;
  if (mask !== undefined) {
    return mask;
  }

  // 8 bits
  mask = geometry.vbstride & 0xff; //assumes it's < 256 floats wide

  const attributes = geometry.attributesInterleaved || geometry.attributes;
  for (let key in attributes) {
    let attr = attributes[key];
    switch (key) {

      // 1 bit
      case "position":{
          if (attr.offset !== 0) {
            console.log("unexpected offset");
          }
          mask |= 1 << 8;
        }
        break;

      //TODO: non-packed normals
      // 2 bits
      case "normal":{
          if (attr.offset !== 3) {
            console.log("unexpected offset");
          }
          if (attr.bytesPerItem === 2) {
            mask |= 1 << 9;
          } else {
            mask |= 2 << 9;
          }
        }
        break;

      // 2 bits
      case "uv":
      case "uvw":{
          if (includeUV) {
            if (attr.offset === 3) {
              mask |= 1 << 11;
            } else if (attr.offset === 4) {
              mask |= 2 << 11;
            } else if (attr.offset === 6) {
              mask |= 3 << 11;
            } else {
              console.log("unexpected offset for color channel", attr.offset);
            }
          }
        }
        break;

      // 3 bits
      case "color":{
          if (includeVC) {
            if (attr.offset === 3) {
              mask |= 1 << 13;
            } else if (attr.offset === 4) {
              mask |= 2 << 13;
            } else if (attr.offset === 6) {
              mask |= 3 << 13;
            } else if (attr.offset === 8) {
              mask |= 4 << 13;
            } else {
              console.log("unexpected offset");
            }

            // We support two types of VertexColors so far:
            //  - 3x float32, RGB (with attrib.normalized=false)
            //  - 4x uint8, RGBX  (with attrib.normalized=true, 4th bit ignored)
            // Both in RGB order. For the mask, we use 1 bit (0: FloatColors, 1: ByteColors)
            const isByteColor = attr.bytesPerItem === 1;
            mask |= (isByteColor ? 1 : 0) << 16;
          }
        }
        break;
    }
  }

  // 2 bits
  let geomType = 0;
  if (includeWideLines) {
    geomType = 2;
  } else if (geometry.isLines) {
    geomType = 1;
  }
  mask |= geomType << 17;

  // NOTE: Whenever adding new bits here, make sure to update BUFFER_LAYOUT_BITS to the new number of required bits.

  geometry.__gpuPipelineHash = mask;

  return mask;
}

/**
 * @param {BufferGeometry} geometry
 * @param {THREE.Matrix3Material} material
 * @param {boolean} includeUV
 * @param {boolean} includeVC
 * @param {boolean} hasTextures
 * @param {boolean} includeWideLines
 * @param {boolean} hasInstanceIdMap
 * @param {boolean} useWboit
 * @param {boolean} idOnly
 * @returns {BigInt}
 */
export function getPipelineHash(
geometry,
material,
includeUV,
includeVC,
hasTextures,
includeWideLines,
hasInstanceIdMap,
useWboit,
idOnly = false)
{
  let geomHash = geometry.__gpuPipelineHash;
  if (geomHash == undefined) {
    geomHash = getBufferLayoutHash(geometry, includeUV, includeVC, includeWideLines);
  }

  let matHash = material.__gpuPipelineHash;
  if (material.needsUpdate || matHash == undefined) {
    matHash = getMaterialHash(material, hasTextures);
  }

  let shaderHash = 0n;
  if (hasInstanceIdMap) {
    shaderHash |= 1n;
  }
  if (useWboit) {
    shaderHash |= 1n << 1n;
  }
  if (idOnly) {
    shaderHash |= 1n << 2n;
  }

  return BigInt(geomHash) | BigInt(matHash) << BUFFER_LAYOUT_BITS | shaderHash << BUFFER_LAYOUT_BITS + MATERIAL_BITS;
}

export function getBufferLayout(geometry, includeNormals, includeUV, includeVC, isUVW, includeWideLines) {
  let buffers = [];

  let hasVertexColor = includeVC && geometry.attributes.color;

  if (geometry.vb) {
    const layout = {
      arrayStride: geometry.vbstride * 4,
      attributes: []
    };

    const attributes = geometry.attributesInterleaved || geometry.attributes;
    if ('position' in attributes) {
      layout.attributes.push({
        shaderLocation: 0,
        offset: attributes.position.offset * 4,
        format: "float32x3"
      });
    }
    //TODO: non-packed normals
    if (!geometry.isLines && includeNormals) {
      layout.attributes.push({
        shaderLocation: 1,
        offset: attributes.normal.offset * 4,
        format: "uint32"
      });
    }

    if (includeUV) {
      if (isUVW) {
        layout.attributes.push({
          shaderLocation: hasVertexColor ? 3 : 2,
          offset: attributes.uvw.offset * 4,
          format: "float32x3"
        });
      } else {
        layout.attributes.push({
          shaderLocation: hasVertexColor ? 3 : 2,
          offset: attributes.uv.offset * 4,
          format: "float32x2"
        });
      }
    }
    if (includeVC) {
      // If bytesPerItem is not specified, we assume float-array element (4 Bytes)
      const bytesPerItem = attributes.color.bytesPerItem || 4;
      const isByteColor = bytesPerItem === 1;
      const isFloatColor = bytesPerItem === 4;
      const format = isByteColor ? "unorm8x4" : "float32x3";
      layout.attributes.push({
        shaderLocation: 2,
        offset: attributes.color.offset * 4,
        format
      });

      if (!isFloatColor && !isByteColor) {
        console.error("Unsupported VertexColor format");
      }
    }

    if (includeWideLines) {
      if (isValidWideLineGeometry(geometry)) {
        layout.attributes.push({
          shaderLocation: 4,
          offset: attributes.prev.offset * 4,
          format: "float32x3"
        });
        layout.attributes.push({
          shaderLocation: 5,
          offset: attributes.next.offset * 4,
          format: "float32x3"
        });
        layout.attributes.push({
          shaderLocation: 6,
          offset: attributes.side.offset * 4,
          format: "float32"
        });
      } else {
        // Note: If this function is called correctly, this should never happen, not even if we actually
        //       have invalid wideline geometry. Because...
        //          1. We check the validity when computing the pipeline hash
        //          2. We check the validity of first geometry when creating a new pipeline with enabled widelines.
        console.error("Invalid wideline geometry.");
      }
    }

    buffers.push(layout);
  } else {
    console.log("non-interleaved buffer geometry not yet done");
  }

  return buffers;
}

function getBufferLayoutHash2D(geometry) {
  geometry.__gpuPipelineHash = geometry.vbstride;
  return geometry.__gpuPipelineHash;
}

export function getBufferLayout2D(geometry) {

  let isCompact = false;

  let buffers = [];

  let layout = {
    arrayStride: geometry.vbstride * 4,
    attributes: []
  };

  if (isCompact) {



    //TODO: only wide layout supported for WebGPU at the moment
  } else {//From VertexBufferBuilder
    /*
    "fields1" :    { offset: 0,                   itemSize: 2, bytesPerItem: 4, divisor: d, normalized: false },
    "fields2" :    { offset: 2,                   itemSize: 4, bytesPerItem: 4, divisor: d, normalized: false },
    "color4b":     { offset: VBB_COLOR_OFFSET,    itemSize: 4, bytesPerItem: 1, divisor: d, normalized: true  },
    "dbId4b":      { offset: VBB_DBID_OFFSET,     itemSize: 4, bytesPerItem: 1, divisor: d, normalized: false },
    "flags4b":     { offset: VBB_FLAGS_OFFSET,    itemSize: 4, bytesPerItem: 1, divisor: d, normalized: false },
    "layerVp4b":   { offset: VBB_LAYER_VP_OFFSET, itemSize: 4, bytesPerItem: 1, divisor: d, normalized: false }
     */

    //fields1
    layout.attributes.push({
      shaderLocation: 0,
      offset: 0,
      format: "float32x2"
    });

    //fields2
    layout.attributes.push({
      shaderLocation: 1,
      offset: 8,
      format: "float32x4"
    });

    //color4b
    layout.attributes.push({
      shaderLocation: 2,
      offset: 24,
      format: "unorm8x4"
    });

    //dbId4b
    layout.attributes.push({
      shaderLocation: 3,
      offset: 28,
      format: "uint8x4"
    });

    //flags4b
    layout.attributes.push({
      shaderLocation: 4,
      offset: 32,
      format: "uint8x4"
    });

    //layerVp4b
    layout.attributes.push({
      shaderLocation: 5,
      offset: 36,
      format: "uint8x4"
    });


    // Needed for elliptical arcs, miter line segments, and 3D line segments.
    // Only include extraParams when geometry has expanded stride (12 floats).
    if (geometry.vbstride >= 12) {
      // extraParams
      layout.attributes.push({
        shaderLocation: 6,
        offset: 40,
        format: "float32x2"
      });
    }
  }


  buffers.push(layout);

  return buffers;
}


/**
 * @typedef {object} MaterialVariations
 * @property {boolean} materialHasLineStyles
 * @property {boolean} isSelectionHighlight
 */

/**
 * @param {LineMaterial} material
 * @returns {MaterialVariations}
 */
export function getMaterialVariations2D(material) {
  return {
    materialHasLineStyles: !!material.hasLineStyles,
    isSelectionHighlight: !!material.defines['SELECTION_RENDERER'],
    loadingAnimation: !!material.defines['LOADING_ANIMATION']
  };
}

const MATERIAL_2D_HASH_BITS = {
  materialHasLineStyles: 1 << 0,
  isSelectionHighlight: 1 << 1,
  loadingAnimation: 1 << 2,
  blendingMask: 0b111 << 3, // Mask for THREE blending mode [0,5].
  depthBiasMask: 0x3F << 6 // 6 bits for depthBias index (0-63)
};

/**
 * @param {BufferGeometry} geometry
 * @param {LineMaterial} material
 * @returns {number}
 */
export function getPipelineHash2D(geometry, material) {
  let geomHash = geometry.__gpuPipelineHash;
  if (geomHash === undefined) {
    geomHash = getBufferLayoutHash2D(geometry);
  }

  let matHash = material.__gpuPipelineHash;
  if (material.needsUpdate || matHash === undefined) {
    matHash = 0;

    const variations = getMaterialVariations2D(material);
    if (variations.materialHasLineStyles) {
      matHash |= MATERIAL_2D_HASH_BITS.materialHasLineStyles;
    }
    if (variations.isSelectionHighlight) {
      matHash |= MATERIAL_2D_HASH_BITS.isSelectionHighlight;
    }
    if (variations.loadingAnimation) {
      matHash |= MATERIAL_2D_HASH_BITS.loadingAnimation;
    }
    // THREE blending mode. TODO: this does not support THREE.CustomBlending yet.
    matHash |= material.blending << 3 & MATERIAL_2D_HASH_BITS.blendingMask;

    // 6 bits for depthBias index (0-63) - computed from global map
    const depthBiasIndex = registerDepthBias(material);
    matHash |= depthBiasIndex << 6 & MATERIAL_2D_HASH_BITS.depthBiasMask;

    material.__gpuPipelineHash = matHash;
  }

  return geomHash | matHash << 24;
}

/**
 * @param {BufferGeometry} geometry
 * @param {Material} material
 * @returns {BigInt}
 */
export function getDefaultPipelineHash(geometry, material) {
  const materialTextureMask = getMaterialTextureMask(material);
  const attributes = geometry.attributes;
  const includeVC = attributes.color && material.vertexColors;
  const hasUV = attributes.uv;
  const hasTextures = hasUV && materialTextureMask !== 0;
  const includeWideLines = false;
  const hasInstanceIdMap = true;
  return getPipelineHash(geometry, material, hasUV, includeVC, hasTextures, includeWideLines, hasInstanceIdMap, USE_WBOIT);
}